/* eslint-disable no-underscore-dangle */
/* eslint-disable consistent-return */
import React, { useState, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';

import {
  TextInput, Select, Checkbox, Form, Button,
} from 'components/common';
import { withTranslation } from 'language/i18n';
import { getObjectByLanguage } from 'language/helpers';
import { emailValidator, nameValidator, passwordValidator } from 'utils/formValidators';
import { register, verifyEmail, updateRegisterStep } from 'models/user';
import { StatusContext } from 'contexts/statusContext';
import KvkkModal from 'components/modals/KvkkModal';
import TermOfUseModal from 'components/modals/TermOfUseModal';
import AcquiescenceModal from 'components/modals/AcquiescenceModal';

const CreateProfile = ({
  t, userState, updateUserState, router, sources,
}) => {
  const { setErrorState } = useContext(StatusContext);

  const [formRef, setFormRef] = useState(useRef());

  const [onResponse, setOnResponse] = useState(false);
  const [kvkkModalVisible, setKvkkModalVisible] = useState(false);
  const [termOfUseModalVisible, setTermOfUseModalVisible] = useState(false);
  const [acquiescenceModalVisible, setAcquiescenceModalVisible] = useState(false);

  const onFormSubmit = async () => {
    if (!onResponse) {
      try {
        setOnResponse(true);
        const { user, token } = await register({
          name: userState.name,
          surname: userState.surname,
          email: userState.email,
          country: userState.country,
          password: userState.password,
          confirmPassword: userState.confirmPassword,
          membershipType: userState.membershipType,
        });
        if (user.id && user.id.length > 5) {
          await updateRegisterStep(token);
          updateUserState({ name: 'id', value: user.id });
          await verifyEmail(user.email);
          router.push({
            pathname: '/register/email-verification',
            query: {
              email: userState.email,
            },
          });
        }
      } catch (error) {
        console.log('registerErr', error);
        if (error.status) {
          setErrorState({
            title: t('alert'),
            text: error.message,
            buttonText: t('close'),
            onClickButton: () => setErrorState({}),
          });
        }
      } finally {
        setOnResponse(false);
      }
    }
  };

  return (
    <div className="col-md-10 col-lg-8 offset-lg-1 mt-5 mt-lg-0">
      <Form
        layout="vertical"
        onFinish={onFormSubmit}
        formRef={(ref) => setFormRef(ref)}
      >

        <div className="fs-22 mb-2">{t('stepNumber', { step: 2 })}</div>
        <div className="fs-22 fw-900 mb-3">{t('enterMembershipInformation')}</div>
        <div className="mb-5" />

        <div className="row mb-3">
          <div className="col-15 col-sm mb-3 mb-sm-0">
            <TextInput
              name="name"
              placeholder={t('name')}
              value={userState.name}
              onChange={updateUserState}
              validator={nameValidator}
              required
            />
          </div>

          <div className="col-15 col-sm">
            <TextInput
              name="surname"
              placeholder={t('surname')}
              value={userState.surname}
              onChange={updateUserState}
              validator={nameValidator}
              required
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-15 col-sm mb-3 mb-sm-0">
            <Select
              name="country"
              placeholder={t('country')}
              onChange={updateUserState}
              value={userState.country}
              options={
                _.map(sources.countries, (country) => {
                  return { value: country.id, text: getObjectByLanguage(country.name) };
                })
              }
              showSearch
              filterOption={(input, opt) => opt.children.toLocaleLowerCase('tr').indexOf(input.toLocaleLowerCase('tr')) >= 0}
              required
            />
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-15 col-sm mb-3 mb-sm-0">
            <TextInput
              name="email"
              type="email"
              placeholder={t('email')}
              value={userState.email}
              onChange={updateUserState}
              validator={emailValidator}
              required
            />
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-15 col-sm mb-3 mb-sm-0">
            <TextInput
              type="password"
              name="password"
              placeholder={t('password')}
              value={userState.password}
              onChange={updateUserState}
              validator={(rule, value) => {
                if (value === userState.confirmPassword) {
                  return passwordValidator(rule, value);
                }
                if (userState.confirmPassword !== '') {
                  return Promise.reject(t('passwordsDoNotMatch'));
                }
              }}
              required
            />
          </div>

          <div className="col-15 col-sm">
            <TextInput
              type="password"
              name="confirmPassword"
              placeholder={t('rePassword')}
              value={userState.confirmPassword}
              onChange={updateUserState}
              validator={(rule, value) => {
                if (value === userState.password) {
                  return passwordValidator(rule, value);
                }

                return Promise.reject(t('passwordsDoNotMatch'));
              }}
              required
            />
          </div>
        </div>

        <div className="row mb-2">
          <div className="col">
            <Checkbox
              required
              name="agreement_messaging"
              value={userState.agreement_messaging}
              onChange={updateUserState}
              text={(
                <>
                  <a
                    className="fw-700 mr-1"
                    onClick={() => setTermOfUseModalVisible(true)}
                  >
                    Sözleşmeyi
                  </a>
                  ve
                  <a
                    className="fw-700 mx-1"
                    onClick={() => setTermOfUseModalVisible(true)}
                  >
                    Kullanım Koşulları'nı
                  </a>
                  okudum, onaylıyorum.
                </>
              )}
            />
          </div>
        </div>

        <div className="row">
          <div className="col">
            <Checkbox
              required
              name="agreement_kvkk"
              value={userState.agreement_kvkk}
              onChange={updateUserState}
              text={(
                <>
                  Kişisel Verilerin İşlenmesi ile ilgili
                  <a
                    className="fw-700 mx-1"
                    onClick={() => setKvkkModalVisible(true)}
                  >
                    Aydınlatma Metni’ni
                  </a>
                  ve
                  <a
                    className="fw-700 mx-1"
                    onClick={() => setAcquiescenceModalVisible(true)}
                  >
                    Rıza Metni'ni
                  </a>
                  okudum.
                </>
              )}
            />
          </div>
        </div>

        <div className="separator separator--horizontal separator--opacity-1 mt-4 mb-4" />

        <div className="fs-13 fw-700 mb-2">Kişisel Verilerin İşlenmesi ve Elektronik Ticari İleti</div>
        <p className="fs-13">
          İşbu form ile paylaştığınız kişisel verileriniz sizlere sunmuş olduğumuz Aydınlatma Metni’nde gösterildiği şekilde işlenecektir.
          Ek olarak, kişisel verilerinizin size özel hizmetler oluşturulması ve tanıtılması için pazarlama faaliyetlerinde kullanılmasını,
          tarafımıza ilettiğiniz iletişim bilgilerinize Şirketimizce reklam, promosyon, vb. ticari elektronik ileti gönderilmesini ve bilgilerinizin
          gönderim için hizmet alınan üçüncü kişilerle paylaşılmasını kabul ediyor musunuz?
        </p>

        <div className="row">
          <div className="col">
            <Checkbox
              containerClassName="mb-4"
              name="agreement_distance_selling"
              value={userState.agreement_distance_selling}
              onChange={updateUserState}
              text={<a className="fw-700 mr-1">Kabul Ediyorum</a>}
              required
            />
          </div>
        </div>

        <Button
          loading={onResponse}
          htmlType="submit"
          className="im-button im-button--primary im-button--fluid im-button--center mt-4 mb-5"
          label={t('next')}
        />
      </Form>
      <KvkkModal
        visible={kvkkModalVisible}
        setVisible={setKvkkModalVisible}
        setAgreement={(value) => {
          updateUserState({ name: 'agreement_kvkk', value });
          formRef.setFieldsValue({
            agreement_kvkk: value,
          });
        }}
      />
      <TermOfUseModal
        visible={termOfUseModalVisible}
        setVisible={setTermOfUseModalVisible}
        setAgreement={(value) => {
          updateUserState({ name: 'agreement_messaging', value });
          formRef.setFieldsValue({
            agreement_messaging: value,
          });
        }}
      />
      <AcquiescenceModal
        visible={acquiescenceModalVisible}
        setVisible={setAcquiescenceModalVisible}
        setAgreement={(value) => {
          updateUserState({ name: 'agreement_kvkk', value });
          formRef.setFieldsValue({
            agreement_kvkk: value,
          });
        }}
      />
    </div>
  );
};

CreateProfile.propTypes = {
  updateUserState: PropTypes.func,
  userState: PropTypes.object,
  router: PropTypes.object,
  t: PropTypes.func,
  sources: PropTypes.object,
};

export default withTranslation('common')(CreateProfile);
